import React, { createContext, useState, useEffect } from 'react';
import { useNotification } from './Notifications';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../features/auth/services/authService';

import AccountSelectionModal from '../../auth/components/AccountSelectionModal';
import signalRService from '../../services/signalRService';
import { InternalMessage, communicationService } from '../../features/admin/services/communicationService';

export type UserRole = 'author' | 'accessor' | 'systemadmin' | 'tagadmin' | 'superadmin' | 'admin' | 'thematicarealeader' | 'dess' | 'journalconvener' | 'tagthematicareamember';

interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  displayRole: string;
  roleId: number;
  menus: string[];
  profileCompleted: boolean;
  availableRoles?: { name: string; roleId: number }[];
  allRoles?: Record<string, string>;
  hasMultipleRoles: boolean;
  isInitialLogin: boolean;
}


interface AuthContextType {
  user: User | null;
  token: string | null;
  menus: string[];
  isAuthenticated: boolean;
  isLoading: boolean;
  pendingRoles: { name: string; roleId: number }[] | null;
  login: (email: string, password: string, rememberMe: boolean) => Promise<void>;
  selectActiveRole: (roleName: string, roleId: number) => void;
  updateProfileCompletion: (completed: boolean) => void;
  triggerAccountSwitch: () => void;
  logout: () => void;
  unreadMessages: InternalMessage[];
  refreshUnread: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | null>(null);

const AUTHOR_MENUS = ['author-dashboard', 'create-manuscript', 'my-submissions'];
const JOURNAL_REP_ROLE_ID = 13;
const JOURNAL_REP_MENUS = ['author-dashboard', 'submission-journal', 'my-submissions'];

interface AuthProviderProps {
  children: React.ReactNode;
}

const mapRoles = (rolesObj?: Record<string, string>) => {
  if (!rolesObj) return [];
  return Object.keys(rolesObj).map(id => ({
    roleId: parseInt(id),
    name: rolesObj[id]
  }));
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [menus, setMenus] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [pendingRoles, setPendingRoles] = useState<{ name: string; roleId: number }[] | null>(null);
  const [pendingUserData, setPendingUserData] = useState<any>(null);
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
  const [isLoginView, setIsLoginView] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState<InternalMessage[]>([]);

  const IDLE_TIMEOUT = 30 * 60 * 1000;
  const WARNING_BEFORE = 2 * 60 * 1000;

  useEffect(() => {
    const storedUser = localStorage.getItem('user') || sessionStorage.getItem('user');
    const storedToken = localStorage.getItem('token') || sessionStorage.getItem('token');

    if (storedUser && storedToken) {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        setMenus(userData.menus || []);
        setToken(storedToken);
      } catch (e) {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('token');
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    let idleTimer: NodeJS.Timeout;
    let warningTimer: NodeJS.Timeout;

    const logoutUser = () => {
      logout();
      showNotification('info', {
        title: 'Session Expired',
        message: 'You have been logged out due to inactivity.',
        timer: 5000,
      });
    };

    const warnUser = () => {
      showNotification('warning', {
        title: 'Session Timeout',
        message: 'Your session will expire in 2 minutes due to inactivity. Move your mouse or press a key to stay logged in.',
        timer: 10000,
      });
    };

    const resetTimers = () => {
      if (token) {
        clearTimeout(idleTimer);
        clearTimeout(warningTimer);
        warningTimer = setTimeout(warnUser, IDLE_TIMEOUT - WARNING_BEFORE);
        idleTimer = setTimeout(logoutUser, IDLE_TIMEOUT);
      }
    };

    if (token) {
      const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

      activityEvents.forEach((event) => window.addEventListener(event, resetTimers));
      resetTimers();

      // Start SignalR globally when token is available
      signalRService.startConnection();

      // Fetch initial unread messages
      const fetchInitialUnread = async () => {
        try {
          const res = await communicationService.getInitialUnread();
          if (res.isSuccess && res.data) {
            setUnreadMessages(res.data);
          }
        } catch (err) {
          }
      };
      fetchInitialUnread();

      // Global message listener for notifications
      const unsubscribe = signalRService.onReceiveMessage((data: any) => {
        // Prevent notifying self
        const senderName = data.senderName || data.SenderName;
        if (senderName && user?.name && senderName.toLowerCase().includes(user.name.split(' ')[0].toLowerCase())) {
          return;
        }

        const msgSubId = data.submissionId || data.SubmissionId;
        const sender = senderName || 'Internal System';
        const message = data.message || data.Message || 'New message received.';

        // Add to unread list
        const newUnread: InternalMessage = {
          id: data.id || Date.now().toString(),
          message: message,
          senderName: sender,
          senderUserId: data.senderUserId || data.SenderUserId,
          sendDateTime: data.sendDateTime || data.SendDateTime || new Date().toISOString(),
          receiverUserId: data.receiverUserId || data.ReceiverUserId,
          submissionId: msgSubId,
          receiverName: data.receiverName || data.ReceiverName
        };
        setUnreadMessages(prev => [newUnread, ...prev]);

        const isDESSOrTagChairman = user?.roleId === 9 || user?.roleId === 5 || user?.role === 'dess';
        const targetRoute = isDESSOrTagChairman
          ? `/admin/prima-facie-reports?openChat=${msgSubId}`
          : `/admin/submissions?openChat=${msgSubId}`;

        showNotification('info', {
          title: 'New Internal Discussion',
          message: `${sender}: ${message.substring(0, 60)}${message.length > 60 ? '...' : ''}`,
          confirmButtonText: 'View Chat',
          onConfirm: () => {
            if (msgSubId) {
              navigate(targetRoute);
            }
          }
        });
      });

      return () => {
        unsubscribe();
        activityEvents.forEach((event) => window.removeEventListener(event, resetTimers));
        clearTimeout(idleTimer);
        clearTimeout(warningTimer);
        // We don't stop connection here to avoid flapping, it's stopped on logout
      };
    } else {
      signalRService.stopConnection();
    }
  }, [token]);

  const login = async (email: string, password: string, rememberMe: boolean): Promise<void> => {
    try {
      const response = await authService.login({ email, password });

      if (response.isSuccess && response.data) {
        const { role, fullName, userId, authToken, username, roleId, profileCompleted, roles, isInitialLogin } = response.data;

        // Multi-role detection
        const roleKeys = Object.keys(roles || {});
        if (roleKeys.length > 1) {
          const mappedRoles = mapRoles(roles);
          setPendingRoles(mappedRoles);
          setPendingUserData({ fullName, userId, authToken, username, profileCompleted, availableRoles: mappedRoles, allRoles: roles});
          setToken(authToken);
          localStorage.setItem('token', authToken); // Fix: Persist token so SwitchAccount can access it
          setIsLoginView(true);
          setIsAccountModalOpen(true);
          return;
        }

        const roleMapping: Record<string, UserRole> = {
          'systemadmin': 'systemadmin',
          'admin': 'admin',
          'author': 'author',
          'tagadmin': 'tagadmin',
          'thematicarealeader': 'thematicarealeader',
          'tagthematicareamember': 'tagthematicareamember',
          'superadmin': 'superadmin',
          'accessor': 'accessor',
          'reviewer': 'accessor',
          'thematic area lead': 'admin',
          'dess': 'dess',
          'journalconvener': 'journalconvener',
        };

        const apiRoleLowercase = role.toLowerCase();
        const isJournalRep = roleId === JOURNAL_REP_ROLE_ID;
        const normalizedRole = isJournalRep ? 'author' : (roleMapping[apiRoleLowercase] || 'admin');
        const userMenus = isJournalRep
          ? JOURNAL_REP_MENUS
          : normalizedRole === 'author' ? AUTHOR_MENUS : [];

        const mappedRoles = mapRoles(roles);
        const newUser: User = {
          id: userId,
          name: fullName,
          email: username,
          role: normalizedRole,
          displayRole: role,
          roleId: roleId,
          menus: userMenus,
          profileCompleted: profileCompleted,
          availableRoles: mappedRoles,
          allRoles: roles,
          hasMultipleRoles: mappedRoles.length > 1,
          isInitialLogin: isInitialLogin ?? false,
        };

        setUser(newUser);
        setMenus(userMenus);
        setToken(authToken);

        // Clear old items first to be safe
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('token');

        // Always use localStorage so auth persists across tabs
        localStorage.setItem('user', JSON.stringify(newUser));
        localStorage.setItem('token', authToken);

        showNotification('success', {
          title: 'Login Successful',
          message: `Welcome back, ${fullName}!`,
          timer: 3000,
        });
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error: any) {
      const message = error.message || error?.response?.data?.message || 'Invalid credentials or network error.';
      showNotification('error', {
        title: 'Login Failed',
        message: message
      });
      throw new Error(message);
    }
  };

  const selectActiveRole = async (roleName: string, roleId: number) => {
    try {
      setLoading(true);
      const response = await authService.switchAccount(roleId);
      
      if (response.isSuccess && response.data) {
        const { role, fullName, userId, authToken, username, roleId: newRoleId, profileCompleted, roles, isInitialLogin } = response.data;

        const roleMapping: Record<string, UserRole> = {
          'systemadmin': 'systemadmin',
          'admin': 'admin',
          'author': 'author',
          'tagadmin': 'tagadmin',
          'thematicarealeader': 'thematicarealeader',
          'tagthematicareamember': 'tagthematicareamember',
          'superadmin': 'superadmin',
          'accessor': 'accessor',
          'reviewer': 'accessor',
          'thematic area lead': 'admin',
          'dess': 'dess',
          'journalconvener': 'journalconvener',
        };

        const isJournalRep = newRoleId === JOURNAL_REP_ROLE_ID;
        const normalizedRole = isJournalRep ? 'author' : (roleMapping[role.toLowerCase()] || 'admin');
        const userMenus = isJournalRep
          ? JOURNAL_REP_MENUS
          : normalizedRole === 'author' ? AUTHOR_MENUS : [];
        const mappedRoles = mapRoles(roles);

        const newUser: User = {
          id: userId,
          name: fullName,
          email: username,
          role: normalizedRole,
          displayRole: role,
          roleId: newRoleId,
          menus: userMenus,
          profileCompleted: profileCompleted,
          availableRoles: mappedRoles,
          allRoles: pendingUserData?.allRoles || {},
          hasMultipleRoles: mappedRoles.length > 1,
          isInitialLogin: isInitialLogin ?? false,
        };

        setUser(newUser);
        setMenus(userMenus);
        setToken(authToken);

        localStorage.setItem('user', JSON.stringify(newUser));
        localStorage.setItem('token', authToken);

        setPendingRoles(null);
        setPendingUserData(null);
        setIsAccountModalOpen(false);

        showNotification('success', {
          title: isLoginView ? 'Login Successful' : 'Account Switched',
          message: isLoginView ? `Welcome back, ${fullName}!` : `Switched to ${role} account`,
          timer: 3000,
        });

        navigate(normalizedRole === 'author' ? '/author' : '/admin');
      } else {
        throw new Error(response.message || 'Switch failed');
      }
    } catch (error: any) {
      showNotification('error', { title: 'Error', message: error.message || 'Failed to switch account' });
    } finally {
      setLoading(false);
    }
  };

  const triggerAccountSwitch = async () => {
    try {
      setLoading(true);
      const response = await authService.getActiveRoles();
      if (response.isSuccess && response.data) {
        const roleKeys = Object.keys(response.data);
        if (roleKeys.length > 1) {
          const mappedRoles = mapRoles(response.data);
          setPendingRoles(mappedRoles);
          setPendingUserData({ 
            fullName: user?.name, 
            userId: user?.id, 
            authToken: token, 
            username: user?.email, 
            profileCompleted: user?.profileCompleted,
            allRoles: user?.allRoles,
          });
          setIsLoginView(false);
          setIsAccountModalOpen(true);
        } else {
          showNotification('info', { title: 'No other accounts', message: 'No other roles are associated with this account.' });
        }
      }
    } catch (error) {
      showNotification('error', { title: 'Error', message: 'Failed to fetch active roles' });
    } finally {
      setLoading(false);
    }
  };

  const updateProfileCompletion = (completed: boolean) => {
    if (user) {
      const updatedUser = { ...user, profileCompleted: completed };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  const logout = (): void => {
    setUser(null);
    setToken(null);
    setMenus([]);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('token');
    setPendingRoles(null);
    setPendingUserData(null);
    setIsAccountModalOpen(false);
    navigate('/login');
  };

  const contextValue: AuthContextType = {
    user,
    token,
    menus,
    isAuthenticated: !!user,
    isLoading: loading,
    pendingRoles,
    login,
    selectActiveRole,
    updateProfileCompletion,
    triggerAccountSwitch,
    logout,
    unreadMessages,
    refreshUnread: async () => {
      try {
        const res = await communicationService.getInitialUnread();
        if (res.isSuccess && res.data) {
          setUnreadMessages(res.data);
        }
      } catch (err) {
        }
    }
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      <AccountSelectionModal
        isOpen={isAccountModalOpen}
        onClose={() => setIsAccountModalOpen(false)}
        roles={pendingRoles || []}
        onSelect={selectActiveRole}
        allowClose={!isLoginView}
      />
    </AuthContext.Provider>
  );
};